import moment from 'src/app/utils/moment';
import { RRule, ByWeekday, Frequency, RRuleSet } from "rrule";

export interface IProgressReportAttachment {
  fileName: string;
  url: string;
  type: string;
  order?: number;
  notes?: string;
}

export class AccountCheckIn {
  status: "new" | "review" | "complete" | "pending";
  id: string;
  userId: string;
  published: string;
  firstPublished?: string;
}

export class  ProgressReport {
  age: number;
  attachments?: {
    [fileNAme: string]: IProgressReportAttachment
  }
  bicepLeft: number;
  bicepRight: number;
  bodyfat: number;
  bodyfatMeasurements?: any;
  calfLeft: number;
  calfRight: number;
  chest: number;
  comments: any[] = [];
  createdBy: string = null;
  creationDate: string = null;
  fat: number;
  followUp?: {
    formDef?: string;
    formId?: string;
    values?: any;
    userForm?: string;
    userFormArchived?: string;
  }
  gender: "male" | "female";
  glutes?:number;
  height?: number;
  title?: string;
  unit: "metric" | "imperial";
  id: string = null;
  lastModifiedBy: string = null;
  lastModifiedDate: string = null;
  lbm: number;
  lastSaved?: string;
  organization?: string;
  progressReportDate: string = null;
  published?: string;
  firstPublished?: string;
  quadLeft: number;
  quadRight: number;
  shoulders: number;
  waist: number;
  weight: number;

  constructor (init: Partial<ProgressReport>){
      Object.assign(this, init);
  }

  getVideoAttachments(): IProgressReportAttachment[] {
    if(this.attachments) {
      const allAttachments = [];
      const keys = Object.keys(this.attachments);
      for (const key of keys) {
        allAttachments.push({
          id: key,
          ...this.attachments[key]
        });
      }
      const videos = allAttachments.filter((attachment) => {
        return attachment.type && attachment.type.indexOf("video") > -1
      });
      return videos && videos.length > 0? videos : null;
    } else {
      return null
    }
  }

  getAllImages() {
    const toReturn = [];
    return [];
  }

  getImageAttachments(): IProgressReportAttachment[] {
    if(this.attachments) {
      const allAttachments = [];
      const keys = Object.keys(this.attachments);
      for (const key of keys) {
        allAttachments.push({
          id: key,
          ...this.attachments[key]
        });
      }
      const images = allAttachments.filter((attachment) => {
        return attachment.type && attachment.type.indexOf("image") > -1
      });
      return images?.length > 0? images : null;
    } else {
      return null
    }
  }

  getImageCount(): number {
    let total = 0;
    if(this.attachments) {
      for (const attachment of Object.values(this.attachments)) {
        if(attachment.type && attachment.type.indexOf("image") > -1) {
          total++;
        }
      }
    }
    return total;
  }

  getVideoCount(): number {
    let total = 0;
    if(this.attachments) {
      for (const attachment of Object.values(this.attachments)) {
        if(attachment.type && attachment.type.indexOf("video") > -1) {
          total++;
        }
      }
    }
    return total;
  }

  getAttachmentCount(): number {
    return this.getImageCount() + this.getVideoCount();
  }

  toJSON() {
    return {
      age: this.age || null,
      bicepLeft: this.bicepLeft || null,
      bicepRight: this.bicepRight || null,
      bodyfat: this.bodyfat || null,
      bodyfatMeasurements: this.bodyfatMeasurements || null,
      calfLeft: this.calfLeft || null,
      calfRight: this.calfRight || null,
      chest: this.chest || null,
      fat: this.fat || null,
      followUp: this.followUp || null,
      gender: this.gender || null,
      glutes: this.glutes || null,
      height: this.height || null,
      lbm: this.lbm || null,
      progressReportDate: this.progressReportDate || null,
      quadLeft: this.quadLeft || null,
      quadRight: this.quadRight || null,
      shoulders: this.shoulders || null,
      title: this.title || null,
      waist: this.waist || null,
      weight: this.weight || null,
      unit: this.unit || null,
      lastSaved: this.lastSaved || null,
    }
  }
}

export class checkinSchedule {
  id: string;
  date: string;
  description: string;
  organization: string;
  user: string;
  timezone: string;
  creationDate: string;
  createdBy: string;
  recurring: any;
  lastModifiedDate: string;
  lastModifiedBy: string;
  start: string;
  compliance: number;

  constructor (init: Partial<checkinSchedule>){
    Object.assign(this, init);
}

getOccurrences(): string[] {
  if(!this.recurring) {
    return []
  }

  function getUntilDate(until: string): Date {
    if(until) {
      return (moment as any)(until).toDate()
    } else {
      return (moment as any)().add(6, "months").toDate()
    }
  }

  function toInt(input: ByWeekday[]): number[] {
    if(!input || input.length === 0) {
      return null
    } else {
      const toReturn = [];
      for (const val of input) {
        switch (val) {
          case "MO":
            toReturn.push(0);
            break;
          case "TU":
            toReturn.push(1);
            break;
          case "WE":
            toReturn.push(2);
            break;
          case "TH":
            toReturn.push(3);
            break;
          case "FR":
            toReturn.push(4);
            break;
          case "SA":
            toReturn.push(5);
            break;
          case "SU":
            toReturn.push(6);
            break;
          default:
            break;
        }
      }
      return toReturn
    }
  }

  function getFreq(frequency: string): Frequency {
    switch (frequency) {
      case "daily":
        return 3
      case "weekly":
        return 2
      case "monthly":
        return 1
    }
  }

  const rruleSet = new RRuleSet(true)
  const ruleOptions: any = {
    freq: getFreq(this.recurring.frequency),
    interval: this.recurring.interval,
    dtstart: (moment as any)(this.start).local().toDate(),
    until: getUntilDate(this.recurring.until)
  }
  if(this.recurring.frequency === "weekly") {
    ruleOptions.byweekday = toInt(this.recurring.byDay);
  }
  if(this.recurring.frequency === "monthly") {
    ruleOptions.bymonthday = this.recurring.byMonthDay
  }
  rruleSet.rrule(new RRule(ruleOptions, true));

  const dates = rruleSet.all();
  const utcOffset = (moment as any)(this.start).utcOffset();
  if(dates && dates.length > 0) {
    const toReturn = [];
    for (const date of dates) {
      toReturn.push((moment as any)(date).utcOffset(utcOffset).format());
    }
    return toReturn;
  }
  return [];
}

  getNextOccurrence() {
    // We want to ensure that we only return then end of day despite hving a precise time
    const occurrences = this.getOccurrences();
    if(occurrences?.length > 0) {
      const filtered = occurrences.filter(occ => {
        return (moment as any)().isBefore((moment as any)(occ).endOf("day"));
      });
      return filtered[0]
    } else {
      return null
    }
  }

  /** First occurrence strictly after now (uses scheduled clock time). */
  getNextUpcomingOccurrence() {
    const occurrences = this.getOccurrences();
    if (!occurrences?.length) {
      return null;
    }
    const now = (moment as any)();
    return occurrences.find((occ) => (moment as any)(occ).isAfter(now)) || null;
  }

  getRecurringTextShort(every = true) {
    let text =`${every? 'Every ' : ''}${this.recurring.interval} ${this.getFrequencyText()}`
    if(this.recurring.interval > 1) {
      text+= "s";
    }
    return text
  }

  getRecurringText() {
    let text =`every ${this.recurring.interval > 1? this.recurring.interval : "" } ${this.getFrequencyText()}`
    if(this.recurring.interval > 1) {
      text+= "s";
    }
    return text
  }

  getFrequencyText() {
    const text = this.recurring?.frequency
    if(text.indexOf("daily") > -1) {
      return "day"
    } else if(text.indexOf("weekly") > -1) {
      return "week"
    } if(text.indexOf("monthly") > -1) {
      return "month"
    } else {
      return text
    }
  }
}

export class CheckinScheduleInstance {
  id: string;
  date: string;
  user: string;
  complete: boolean;
  start: string;
  status: "scheduled" | "complete";
}